import React, {useEffect, useState} from 'react';
import {Link, useLocation, useNavigate} from "react-router-dom";
import {useAuth} from "../auth/AuthContext";
import axios from 'axios';
import {Alert, Box, Button, Link as MuiLink, Paper, TextField, Typography} from '@mui/material';
import {API_ENDPOINT} from '../config';
import AltchaVerification from '../components/AltchaVerification';

const decodeRoles = (token) => {
    try {
        const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        const payload = JSON.parse(window.atob(base64));
        return payload.roles || [];
    } catch (e) {
        return [];
    }
};

const LoginPage = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    // Pasul 34: soluția ALTCHA se consumă la fiecare încercare; după eșec widgetul se remontează (key nou).
    const [altcha, setAltcha] = useState('');
    const [captchaVersion, setCaptchaVersion] = useState(0);

    const navigate = useNavigate();
    const location = useLocation();
    const {auth, setAuthData} = useAuth();

    useEffect(() => {
        if (localStorage.getItem('authToken')) {
            const redirectAfterLogin = location.state?.from || '/admin';
            navigate(redirectAfterLogin, { replace: true });
        }
    }, [location, navigate, auth]);

    const handleLogin = async (e) => {
        e.preventDefault();
        if (!altcha || loading) return;
        setError(null);
        setLoading(true);

        try {
            const response = await axios.post(`${API_ENDPOINT}/login`, { email, password, altcha });
            const { token, refresh_token } = response.data;
            const roles = decodeRoles(token);

            if (refresh_token) {
                localStorage.setItem('refreshToken', refresh_token);
            }
            setAuthData(token, roles);

        } catch (error) {
            setAltcha('');
            setCaptchaVersion((v) => v + 1);
            if (error.response?.status === 401 || error.response?.status === 400) {
                setError(error.response?.data?.message || 'Email sau parolă greșită.');
            } else if (error.response?.status === 429) {
                const message = error.response?.data?.message;
                setError(typeof message === 'string' && message.trim()
                    ? message
                    : 'Prea multe încercări de autentificare. Încearcă din nou mai târziu.');
            } else if (error.response) {
                setError('Eroare de server. Încearcă din nou mai târziu.');
            } else {
                setError('Eroare de conexiune la server. Încearcă din nou.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{width: 360, maxWidth: '100%', mx: 'auto'}}>
            <Typography variant="h1" sx={{fontSize: 32, textAlign: 'center', mb: 3}}>Bine ai revenit.</Typography>
            <Paper variant="outlined" sx={{p: {xs: 2.5, sm: 3}, borderRadius: 1.5,
                backgroundColor: 'color-mix(in srgb, var(--gallery-paper) 65%, transparent)',
                '& .MuiOutlinedInput-root': {fontSize: 14, borderRadius: 1,
                    '& fieldset': {borderColor: 'divider'}},
                '& .MuiInputLabel-root': {fontSize: 14},
                // Override MUI's dark autofill blue as well as browser autofill.
                '&& input:-webkit-autofill, && input:-webkit-autofill:hover, && input:-webkit-autofill:focus': {
                    WebkitBoxShadow: '0 0 0 1000px var(--gallery-paper) inset',
                    boxShadow: '0 0 0 1000px var(--gallery-paper) inset',
                    WebkitTextFillColor: 'var(--gallery-text)', caretColor: 'var(--gallery-text)',
                },
            }}>
                <Typography sx={{color: 'text.secondary', textAlign: 'center', fontSize: 14, mb: 3}}>Administrare</Typography>
                {error && <Alert severity="error" sx={{mb: 2}}>{error}</Alert>}
                <Box component="form" onSubmit={handleLogin} sx={{display: 'grid', gap: 2.5}}>
                    <TextField label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                        size="small" required autoComplete="email" fullWidth />
                    <TextField label="Parolă" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                        size="small" required autoComplete="current-password" fullWidth />
                    <AltchaVerification key={captchaVersion} onVerified={setAltcha} />
                    <Button type="submit" variant="outlined" disabled={loading || !altcha} fullWidth sx={{py: .85, fontSize: 14, borderRadius: 1}}>
                        {loading ? 'Se autentifică...' : 'Autentificare'}
                    </Button>
                </Box>
                <Box sx={{textAlign: 'center', mt: 3}}>
                    <MuiLink component={Link} to="/parola-uitata" underline="hover" sx={{display: 'block', mb: 1.5, fontSize: 13, color: 'primary.main', '&:hover': {color: 'primary.main'}}}>Ai uitat parola?</MuiLink>
                    <MuiLink component={Link} to="/" underline="hover" sx={{fontSize: 13, color: 'primary.main', '&:hover': {color: 'primary.main'}}}>← Acasă</MuiLink>
                </Box>
            </Paper>
        </Box>
    );
};

export default LoginPage;
